import React, { createContext, useContext, useEffect, useState } from "react";
import {
  getFirestore,
  collection,
  query,
  where,
  getDocs,
  addDoc,
  setDoc,
  updateDoc,
  deleteDoc,
  doc,
  getDoc,
} from "firebase/firestore";
import { useUserData } from "../context/UserDataFetcher";
import type { Truck } from "../types/DB2";
import dbPrefixes from "../config";

type TrucksCtx = {
  trucks: Truck[];
  refresh: () => Promise<void>;
  createTruck: (payload: Partial<Truck>) => Promise<void>;
  updateTruck: (id: string, payload: Partial<Truck>) => Promise<void>;
  deleteTruck: (id: string) => Promise<void>;
};

const TrucksContext = createContext<TrucksCtx | undefined>(undefined);

export const TrucksDataProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [trucks, setTrucks] = useState<Truck[]>([]);
  const userData = useUserData();
  const db = getFirestore();
  const dbPrefix = dbPrefixes.productionDbPrefix;

  const fetchTrucks = async () => {
    if (!userData) {
      setTrucks([]);
      return;
    }

    try {
      const trucksRef = collection(db, `${dbPrefix}trucks`);
      let q;
      if (userData.role === "manufacturer" && userData.manufacturerId) {
        q = query(trucksRef, where("manufacturerId", "==", userData.manufacturerId));
      } else if (userData.role === "dealer" && userData.dealerId) {
        q = query(trucksRef, where("truckCompanyId", "==", userData.dealerId));
      } else {
        setTrucks([]);
        return;
      }

      const snap = await getDocs(q);
      const list: Truck[] = [];
      snap.forEach((d) => {
        const data = d.data() as any;
        list.push({ ...(data as Truck) });
      });
      setTrucks(list);
    } catch (err) {
      console.error("TrucksDataProvider: fetchTrucks failed", err);
      setTrucks([]);
    }
  };

  useEffect(() => {
    // fetch when userData changes (role / ids)
    fetchTrucks();
  }, [userData?.manufacturerId, userData?.dealerId, userData?.role]);

  const sanitizeDocId = (plateRaw: string) =>
    plateRaw.replace(/\//g, "-").replace(/\s+/g, "-");

  const createTruck = async (payload: Partial<Truck>) => {
    const payloadToSave = { ...(payload as any) };
    // allow string lat,lng for convenience
    if (typeof payloadToSave.currentGeo === "string") {
      const [latStr = "", lngStr = ""] = payloadToSave.currentGeo
        .split(",")
        .map((s: string) => s.trim());
      const lat = parseFloat(latStr);
      const lng = parseFloat(lngStr);
      if (!Number.isNaN(lat) && !Number.isNaN(lng)) {
        payloadToSave.currentGeo = { latitude: lat, longitude: lng };
      } else {
        delete payloadToSave.currentGeo;
      }
    }

    // default manufacturer / truckCompany id from user context if missing
    if (!payloadToSave.manufacturerId && userData?.manufacturerId)
      payloadToSave.manufacturerId = userData.manufacturerId;
    if (!payloadToSave.truckCompanyId && userData?.dealerId)
      payloadToSave.truckCompanyId = userData.dealerId;

    try {
      const trucksRef = collection(db, `${dbPrefix}trucks`);
      const plateRaw = (payloadToSave.licensePlate ?? "").toString().trim();

      if (plateRaw.length > 0) {
        // sanitize doc id (avoid path separators)
        const docId = sanitizeDocId(plateRaw);
        await setDoc(doc(db, `${dbPrefix}trucks`, docId), payloadToSave);
      } else {
        // fallback to auto-id if no license plate provided
        await addDoc(trucksRef, payloadToSave);
      }

      await fetchTrucks();
    } catch (err) {
      console.error("TrucksDataProvider: createTruck failed", err);
      throw err;
    }
  };

  const updateTruck = async (id: string, payload: Partial<Truck>) => {
    try {
      const oldRef = doc(db, `${dbPrefix}trucks`, id);
      const oldSnap = await getDoc(oldRef);
      if (!oldSnap.exists()) {
        throw new Error("Truck to update not found");
      }
      const oldData = oldSnap.data() as any;

      // normalize and handle currentGeo string
      const toSave: any = { ...(payload as any) };
      if (typeof toSave.currentGeo === "string") {
        const [latStr = "", lngStr = ""] = toSave.currentGeo
          .split(",")
          .map((s: string) => s.trim());
        const lat = parseFloat(latStr);
        const lng = parseFloat(lngStr);
        if (!Number.isNaN(lat) && !Number.isNaN(lng)) {
          toSave.currentGeo = { latitude: lat, longitude: lng };
        } else {
          delete toSave.currentGeo;
        }
      }

      // if licensePlate is being changed (and non-empty), we need to move the doc
      const incomingPlate = (toSave.licensePlate ?? "").toString().trim();
      if (incomingPlate.length > 0) {
        const newDocId = sanitizeDocId(incomingPlate);

        if (newDocId !== id) {
          // ensure we don't clobber an existing different truck
          const newRef = doc(db, `${dbPrefix}trucks`, newDocId);
          const newSnap = await getDoc(newRef);
          if (newSnap.exists()) {
            throw new Error("A truck with that license plate already exists");
          }

          // merge old data with incoming changes (incoming takes precedence)
          const merged = { ...oldData, ...toSave, licensePlate: incomingPlate };

          // create new doc and delete old doc
          await setDoc(newRef, merged);
          await deleteDoc(oldRef);
          await fetchTrucks();
          return;
        }
        // else newDocId === id, fall through to update below
      }

      // normal update when id remains same (or no licensePlate change)
      await updateDoc(oldRef, toSave);
      await fetchTrucks();
    } catch (err) {
      console.error("TrucksDataProvider: updateTruck failed", err);
      throw err;
    }
  };

  const deleteTruck = async (id: string) => {
    try {
      const d = doc(db, `${dbPrefix}trucks`, id);
      await deleteDoc(d);
      await fetchTrucks();
    } catch (err) {
      console.error("TrucksDataProvider: deleteTruck failed", err);
      throw err;
    }
  };

  return (
    <TrucksContext.Provider
      value={{ trucks, refresh: fetchTrucks, createTruck, updateTruck, deleteTruck }}
    >
      {children}
    </TrucksContext.Provider>
  );
};

export const useTrucksData = () => {
  const ctx = useContext(TrucksContext);
  if (!ctx) throw new Error("useTrucksData must be used within TrucksDataProvider");
  return ctx;
};

export default TrucksDataProvider;